import React, { useEffect, useState } from 'react';
import UploadProduct from '../components/UploadProduct';
import SummaryApi from '../common';
import AdminProductCard from '../components/AdminProductCard';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import AdminEditProduct from '../components/AdminEditProduct';
import { useTable } from 'react-table';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import displayINRCurrency from '../helpers/displayCurrency';

const AllProducts = () => {
  const [openUploadProduct, setOpenUploadProduct] = useState(false);
  const [allProduct, setAllProduct] = useState([]);
  const [viewMode, setViewMode] = useState('icons'); // 'icons' or 'list'
  const [editProduct, setEditProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchAllProduct = async () => {
    try {
      const response = await fetch(SummaryApi.adminProduct.url, {
        headers: {
          'Authorization': 'Bearer oakyaecom_no_units_916',
          'Content-Type': 'application/json',
        },
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
  
      const dataResponse = await response.json();
  
      const reversedProducts = (dataResponse?.data || []).reverse();
      setAllProduct(reversedProducts);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(`Failed to fetch products: ${error.message}`);
    }
  };
  
  useEffect(() => {
    fetchAllProduct();
  }, []);

  const handleEditProduct = (product) => {
    setEditProduct(product);
  };

  const handleSaveProduct = () => {
    fetchAllProduct();
    setEditProduct(null);
  };

  const filteredProducts = allProduct.filter(product =>
    (product.product_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (product.product_code || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const downloadExcel = () => {
    if (filteredProducts.length === 0) {
      toast.info("No products to export");
      return;
    }

    const ws = XLSX.utils.json_to_sheet(filteredProducts.map(product => ({
      'Product Code': product.product_code,
      'Created Date': new Date(product.created_date).toLocaleDateString('en-IN'),
      'Product Name': product.product_name,
      'Product Sub Title': product.sub_title,
      'Product Description': product.product_description,
      'Price': `₹ ${typeof product.price === 'number' ? product.price.toLocaleString('en-IN') : product.price}`,
      'Discount(%)': product.discount,
      'After Discount': `₹ ${typeof product.after_discount_price === 'number' ? product.after_discount_price.toLocaleString('en-IN') : product.after_discount_price}`,
      'Rating': product.rating,
      'Status': product.is_active === '1' ? 'Active' : 'Inactive',
      'Display': product.is_display === '1' ? 'True' : 'False',
      'Best Seller': product.best_seller === '1' ? 'True' : 'False',
      'Home Display': product.home_display === '1' ? 'True' : 'False',
    })), { header: ["Product Code", "Created Date", "Product Name", "Product Sub Title", "Product Description", "Price", "Discount(%)", "After Discount", "Rating", "Status", "Display", "Best Seller", "Home Display"] });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, 'Products.xlsx');
  };

  const columns = React.useMemo(
    () => [
      { Header: 'Product Code', accessor: 'product_code' },
      {
        Header: 'Created Date',
        accessor: 'created_date',
        Cell: ({ value }) => new Date(value).toLocaleDateString('en-IN'),
      },
      { Header: 'Product Name', accessor: 'product_name' },
      { Header: 'Product Sub Title', accessor: 'sub_title' },
      { Header: 'Product Description', accessor: 'product_description' },
      {
        Header: 'Price',
        accessor: 'price',
        Cell: ({ value }) => <span className="font-medium">{displayINRCurrency(value)}</span>,
      },
      { Header: 'Discount(%)', accessor: 'discount' },
      {
        Header: 'After Discount',
        accessor: 'after_discount_price',
        Cell: ({ value }) => <span className="font-medium">{displayINRCurrency(value)}</span>,
      },
      { Header: 'Rating', accessor: 'rating' },
      {
        Header: 'Status',
        accessor: 'is_active',
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${value === '1' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value === '1' ? 'Active' : 'Inactive'}
          </span>
        ),
      },
      {
        Header: 'Display',
        accessor: 'is_display',
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${value === '1' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value === '1' ? 'True' : 'False'}
          </span>
        ),
      },
      {
        Header: 'Best Seller',
        accessor: 'best_seller',
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${value === '1' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value === '1' ? 'True' : 'False'}
          </span>
        ),
      },
      {
        Header: 'Home Display',
        accessor: 'home_display',
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${value === '1' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value === '1' ? 'True' : 'False'}
          </span>
        ),
      },
      {
        Header: 'Action',
        id: 'action',
        Cell: ({ row }) => (
          <button
            onClick={() => handleEditProduct(row.original)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-1.5 px-4 rounded transition"
          >
            Edit
          </button>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: filteredProducts });

  const showEmptyState = allProduct.length === 0 || (searchQuery && filteredProducts.length === 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className='bg-white py-3 px-4 flex flex-col sm:flex-row justify-between items-center shadow-sm'>
        <h2 className='font-bold text-xl text-gray-800'>All Products</h2>

        <div className='flex flex-wrap items-center gap-4 mt-3 sm:mt-0'>
          <div className='flex items-center text-sm text-gray-600'>
            <span>Active</span>
            <FaCheckCircle className='mx-2 text-lg text-green-600' />
            <span className='ml-3'>Inactive</span>
            <FaTimesCircle className='mx-2 text-lg text-red-600' />
          </div>
          <button
            className='border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-all py-1.5 px-4 rounded-full font-medium'
            onClick={() => setOpenUploadProduct(true)}
          >
            Upload Product
          </button>
          <button
            className='border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all py-1.5 px-4 rounded-full font-medium'
            onClick={() => {
              setViewMode(viewMode === 'icons' ? 'list' : 'icons');
              setSearchQuery('');
            }}
          >
            {viewMode === 'icons' ? 'Switch to List' : 'Switch to Grid'}
          </button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 px-4 mt-5 mb-6">
        <input
          type="text"
          placeholder="Search by name or product code..."
          className="flex-grow p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        <div className="flex gap-3">
          <button
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2.5 px-5 rounded-lg transition"
            onClick={() => setSearchQuery('')}
          >
            Clear
          </button>
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-5 rounded-lg transition"
            onClick={downloadExcel}
            disabled={filteredProducts.length === 0}
          >
            Download Excel
          </button>
        </div>
      </div>

      {showEmptyState ? (
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
          <img
            src="https://img.freepik.com/free-vector/no-data-concept-illustration_114360-536.jpg"
            alt="No products found"
            className="w-64 md:w-80 mb-6 opacity-90"
          />
          <h3 className="text-xl font-semibold text-gray-700 mb-3">
            {allProduct.length === 0 ? "No Products Added Yet" : "No Products Found"}
          </h3>
          <p className="text-gray-500 mb-8 max-w-md">
            {allProduct.length === 0
              ? "Start by adding your first product using the button above."
              : "Try adjusting your search or clear the filter to see all products."}
          </p>

          {allProduct.length === 0 && (
            <button
              className='bg-red-600 text-white hover:bg-red-700 transition-all py-3 px-8 rounded-full font-medium shadow-md'
              onClick={() => setOpenUploadProduct(true)}
            >
              Add Your First Product
            </button>
          )}
        </div>
      ) : viewMode === 'icons' ? (
        <div className='flex flex-wrap gap-5 py-6 px-4'>
          {filteredProducts.map((product, index) => (
            <AdminProductCard
              data={product}
              key={index + "allProduct"}
              fetchdata={fetchAllProduct}
              onEdit={() => handleEditProduct(product)}
            />
          ))}
        </div>
      ) : (
        <div className='overflow-x-auto px-4 pb-8'>
          <table {...getTableProps()} className='min-w-full bg-white shadow rounded-lg overflow-hidden'>
            <thead className='bg-gray-100'>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps()}
                      className='px-4 py-3 text-left text-sm font-semibold text-gray-700'
                    >
                      {column.render('Header')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="divide-y divide-gray-200">
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className='hover:bg-gray-50 transition'>
                    {row.cells.map(cell => (
                      <td
                        {...cell.getCellProps()}
                        className='px-4 py-3 text-sm text-gray-800'
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {openUploadProduct && (
        <UploadProduct
          onClose={() => setOpenUploadProduct(false)}
          fetchData={fetchAllProduct}
        />
      )}

      {editProduct && (
        <AdminEditProduct
          productData={editProduct}
          onClose={() => setEditProduct(null)}
          fetchdata={fetchAllProduct}
        />
      )}
    </div>
  );
};

export default AllProducts;